'use client';

import { useStaticQuery, graphql } from 'gatsby';
import { useState, useEffect } from 'react';

/**
 * Custom hook to fetch and filter a list of pages from Storyblok.
 *
 * @param {string} currentStoryInternalId - The internal ID of the current story.
 * @param {boolean} sortByTags - Flag indicating whether to sort the pages by tags.
 * @returns {Array} - The filtered list of pages.
 */

export const useGetPageList = (currentStoryInternalId, sortByTags) => {
  const data = useStaticQuery(graphql`
    {
      pages: allStoryblokEntry(sort: { created_at: DESC }) {
        edges {
          node {
            created_at
            id
            uuid
            name
            slug
            full_slug
            tag_list
            internalId
            field_listing_title_string
            field_listing_ingress_string
            lang
          }
        }
      }
      space: allStoryblokSpace(sort: { name: ASC }) {
        edges {
          node {
            name
            internalId
          }
        }
      }
    }
  `);

  const [pages, setPages] = useState([]);

  useEffect(() => {
    const getPages = async () => {
      const filteredPageResponse = data.pages.edges.filter((edge) => edge);
      const filteredSpaceResponse = data.space.edges.filter((edge) => edge);

      // The root page is the page that the page listing is based on, e.g. "aktuellt" in "/aktuellt/nyheter":
      const rootPage = filteredPageResponse.find(
        (page) =>
          page.node.internalId === currentStoryInternalId &&
          page.node.lang === process.env.GATSBY_STORYBLOK_LANGUAGE,
      );

      // Default sorting active --- --- --- --- --- --- --- --->

      // Returns the depth of the page in the page tree:
      const getPageDepth = (page) => {
        const depth = page.node.full_slug.split('/').filter(Boolean).length;
        return depth;
      };

      function checkPageGroup(page) {
        const rootPageDepth = getPageDepth(rootPage);
        const pageDepth = getPageDepth(page);

        // If the page is found below the root page, return the page:
        if (
          page.node.full_slug.includes(rootPage.node.full_slug) &&
          pageDepth === rootPageDepth + 1
        ) {
          return page;
        } else {
          return null;
        }
      }

      // >--- --- --- --- --- --- --- --- Default sorting active

      // Sorting by tags active --- --- --- --- --- --- --- --->

      // Compares the tags of the page with the tags of the root page, returns the page if there is a match:
      // TODO: This function is a WIP. For now, it only checks if the page has some of the tags of the root page.
      function checkTagMatch(page) {
        const tagMatch = rootPage.node.tag_list.some((tag) =>
          page.node.tag_list.includes(tag),
        );

        if (tagMatch) {
          return page;
        } else {
          return null;
        }
      }

      // >--- --- --- --- --- --- --- --- Sorting by tags active

      // The teaser list pages are the pages that holds the teasers, and should not be included in the list of pages to display when sorting by tags:
      /*const teaserListPages = filteredPageResponse.filter((page) =>
        page.node.content.includes('page_listing'),
      );*/

      function excludePage(page) {
        const isTeaserListPage = false;

        //const isTeaserListPage = teaserListPages.includes(page);

        if (
          // As long as the page is not identical to the root page and is not the site's start page or one of the teaser list pages, return the page:
          page !== rootPage &&
          page.node.full_slug !== 'home' &&
          !isTeaserListPage &&
          page.node.lang === process.env.GATSBY_STORYBLOK_LANGUAGE
        ) {
          return page;
        } else {
          return null;
        }
      }

      const filteredPageGroup = !sortByTags
        ? filteredPageResponse.filter(checkPageGroup)
        : filteredPageResponse.filter(checkTagMatch);
      const filteredPages = filteredPageGroup.filter(excludePage);

      // Adds the space ID to the page object:
      setPages(() =>
        filteredPages.map((page) => ({
          ...page,
          node: {
            ...page.node,
            space: filteredSpaceResponse[0].node.internalId,
          },
        })),
      );
    };

    getPages();
  }, [data.pages.edges, currentStoryInternalId, sortByTags, data.space.edges]);

  return pages;
};
